//react
import { useState, useCallback, useEffect } from "react";

// material ui components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";

// components
import AddressModification from "../../components/userProfile/address";
import { MobileNumberData } from "../../components/userProfile/mobile-number";
import PaymentMethod from "../../components/userProfile/paymentMethod";
import PersonalData from "../../components/userProfile/personalData";
import SecurityCode from "../../components/userProfile/securityCode";

// providers context
import { AuthConfig } from "../../setup/context/config/authConfig";

// styles
import styles from "./styles.module.css";
import "./index.css";

// apis
import { getBasicUserDetailsById } from "../../setup/api/services/user";

// types
import { IUserInfoResponse } from "../../setup/api/services/user/models";
import ProfileList from "./ProfileList";
import { useSearchParams } from "react-router-dom";
import { PROFILE_PATH_PARAMS } from "../../setup/constants";

export enum SelectedTab {
  PROFILE_TAB = "PROFILE_TAB",
  MOBILE_TAB = "MOBILE_TAB",
  SECURITY_TAB = "SECURITY_TAB",
  PAYMENT_TAB = "PAYMENT_TAB",
  ADDRESS_TAB = "ADDRESS_TAB",
}
const UserProfile = (): JSX.Element => {
  const userId = AuthConfig?.getUserInfo()?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const path = searchParams.get("path");
  useEffect(() => {
    switch (path) {
      case PROFILE_PATH_PARAMS.ME:
        setSelectedTab(SelectedTab.PROFILE_TAB);
        return;
      case PROFILE_PATH_PARAMS.ADDRESS:
        setSelectedTab(SelectedTab.ADDRESS_TAB);
        return;
      case PROFILE_PATH_PARAMS.PAYMENT_METHOD:
        setSelectedTab(SelectedTab.PAYMENT_TAB);
        return;
      case PROFILE_PATH_PARAMS.PIN_CODE:
        setSelectedTab(SelectedTab.SECURITY_TAB);
        return;
      case PROFILE_PATH_PARAMS.MOBILE_NUMBER:
        setSelectedTab(SelectedTab.MOBILE_TAB);
        return;
      default:
        return setSelectedTab(SelectedTab.PROFILE_TAB);
    }
  }, [path]);
  const [selectedTab, setSelectedTab] = useState<SelectedTab>(
    SelectedTab.PROFILE_TAB
  );
  const [userDetails, setUserDetials] = useState<
    IUserInfoResponse["data"] | undefined
  >(undefined);

  const fetchUserDetails = useCallback(async (userId: number) => {
    if (userId) {
      try {
        const response = await getBasicUserDetailsById(Number(userId));
        setUserDetials(response.data);
      } catch (error) {
        console.error("error", error);
      }
    }
  }, []);

  useEffect(() => {
    if (userId) fetchUserDetails(Number(userId));
  }, [fetchUserDetails, userId]);

  const PageDisplay = () => {
    switch (selectedTab) {
      case SelectedTab.PROFILE_TAB:
        return (
          <PersonalData
            profileFullName={userDetails?.profile?.fullName || ""}
            photoUrl={userDetails?.profile?.photoUrl || ""}
            phoneNumber={userDetails?.phoneNumber || ""}
          />
        );
      case SelectedTab.MOBILE_TAB:
        return <MobileNumberData />;
      case SelectedTab.SECURITY_TAB:
        return <SecurityCode />;
      case SelectedTab.PAYMENT_TAB:
        return <PaymentMethod />;
      case SelectedTab.ADDRESS_TAB:
        return <AddressModification />;
      default:
        return (
          <PersonalData
            profileFullName={userDetails?.profile?.fullName}
            photoUrl={userDetails?.profile?.photoUrl}
            phoneNumber={userDetails?.phoneNumber}
          />
        );
    }
  };

  const handleSelection = useCallback(
    (selected: SelectedTab) => {
      setSelectedTab(selected);
      switch (selected) {
        case SelectedTab.PROFILE_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.ME });
          return;
        case SelectedTab.MOBILE_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.MOBILE_NUMBER });
          return;
        case SelectedTab.SECURITY_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.PIN_CODE });
          return;
        case SelectedTab.PAYMENT_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.PAYMENT_METHOD });
          return;
        case SelectedTab.ADDRESS_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.ADDRESS });
          return;
        default:
          return;
      }
    },
    [setSearchParams]
  );

  return (
    <Box className={styles["container"]}>
      <Container>
        <Grid container gap={1}>
          <Grid md={3} xs={12}>
            <ProfileList
              selectedTab={selectedTab}
              handleSelection={handleSelection}
            />
          </Grid>
          <Grid md={8} xs={12}>
            <Box className={styles["card"]}>{PageDisplay()}</Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default UserProfile;
